'use client';

import React, { forwardRef, useState, useEffect, useRef, useMemo } from 'react';
// Remove the usePlacesAutocomplete import
// import usePlacesAutocomplete from 'use-places-autocomplete';
import { Autocomplete, AutocompleteInput, AutocompleteList, AutocompleteItem } from '@components/ui/autocomplete';
import { useCitiesStore } from '@/store/citiesStore';
import { trackEvent } from '@/lib/analytics';
import { debounce } from 'lodash-es'; // Assuming lodash-es is available or can be added

const CityAutocomplete = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>((props, ref) => {
  const { addCityByAddress, cities } = useCitiesStore();
  // State for input value, suggestions, loading status, and session token
  const [value, setValue] = useState('');
  // Update state to hold PlacePrediction objects
  const [suggestions, setSuggestions] = useState<google.maps.places.PlacePrediction[]>([]);
  const [status, setStatus] = useState<google.maps.places.PlacesServiceStatus | ''>('');
  const [loading, setLoading] = useState(false);
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  // const autocompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(null); // No longer needed

  // Check if Google Maps API is loaded
  useEffect(() => {
    if (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places) {
      setIsGoogleReady(true);
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
      // Note: While we use AutocompleteSuggestion.fetchAutocompleteSuggestions,
      // the structure might still internally leverage aspects accessible via the service instance,
      // or we might adapt this depending on the exact API capabilities used.
      // For now, let's focus on fetchAutocompleteSuggestions.
    } else {
      console.warn("Google Maps Places API not loaded yet.");
      // Optionally implement a retry mechanism or wait for a global event
    }
  }, []);

  // Debounced fetch function
  const fetchSuggestions = useMemo(
    () =>
      debounce(async (inputValue: string) => {
        if (!isGoogleReady || !inputValue || !sessionTokenRef.current) {
          setSuggestions([]);
          setStatus('');
          setLoading(false);
          return;
        }

        setLoading(true);
        setStatus('');

        // Use AutocompleteRequest type. Remove 'types' property as it's not valid here.
        const request: google.maps.places.AutocompleteRequest = {
          input: inputValue,
          sessionToken: sessionTokenRef.current,
          // types: ['(regions)'], // 'types' is not valid on AutocompleteRequest
          // includedPrimaryTypes: ['locality', 'administrative_area_level_3'], // Use this if type filtering is needed
          // language: 'en', // Example: Explicitly set language if needed
          // Optionally add locationBias or componentRestrictions if needed
          // componentRestrictions: { country: ['us'] } // Example restriction
        };

        // Using AutocompleteSuggestion.fetchAutocompleteSuggestions (new approach)
        try {
          const { suggestions: fetchedSuggestions } =
            await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
          // Filter out null placePredictions and ensure the correct type (PlacePrediction)
          const validPredictions = fetchedSuggestions
            .map(s => s.placePrediction)
            .filter((p): p is google.maps.places.PlacePrediction => p !== null);
          setSuggestions(validPredictions);
          setStatus(google.maps.places.PlacesServiceStatus.OK);
        } catch (error) {
          setSuggestions([]);
          // Attempt to map error to a status, otherwise use UNKNOWN_ERROR
          // Note: The new API might throw errors differently than setting status codes.
          // We'll need to refine error handling based on observed behavior.
          let errorStatus = google.maps.places.PlacesServiceStatus.UNKNOWN_ERROR;
          if (error instanceof Error) {
            console.error("Autocomplete prediction error:", error.message);
            // Basic mapping based on potential error messages or types
            if (error.message.includes("INVALID_REQUEST")) {
              errorStatus = google.maps.places.PlacesServiceStatus.INVALID_REQUEST;
            } else if (error.message.includes("REQUEST_DENIED")) {
              errorStatus = google.maps.places.PlacesServiceStatus.REQUEST_DENIED;
            } else if (error.message.includes("ZERO_RESULTS")) {
              errorStatus = google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
            } // Add more specific error handling as needed
          } else {
            console.error("Autocomplete prediction failed with unknown error:", error);
          }
          setStatus(errorStatus);

          // Potentially renew token on error if needed
          if (
            errorStatus === google.maps.places.PlacesServiceStatus.INVALID_REQUEST ||
            errorStatus === google.maps.places.PlacesServiceStatus.REQUEST_DENIED
          ) {
            sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
          }
        } finally {
          setLoading(false);
        }

        /* Old AutocompleteService implementation - removing this block
        if (!autocompleteServiceRef.current) {
           autocompleteServiceRef.current = new google.maps.places.AutocompleteService();
        }

        autocompleteServiceRef.current.getPlacePredictions(request, (predictions, serviceStatus) => {
            setLoading(false);
            if (serviceStatus === google.maps.places.PlacesServiceStatus.OK && predictions) {
              setSuggestions(predictions);
              setStatus(google.maps.places.PlacesServiceStatus.OK);
            } else {
              setSuggestions([]);
              setStatus(serviceStatus);
              console.error("Autocomplete prediction failed:", serviceStatus);
              // Potentially renew token on error if needed
              if (serviceStatus === google.maps.places.PlacesServiceStatus.INVALID_REQUEST || serviceStatus === google.maps.places.PlacesServiceStatus.REQUEST_DENIED) {
                 sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
              }
            }
          }
        );
        */
      }, 300),
    [isGoogleReady]
  );

  useEffect(() => () => fetchSuggestions.cancel(), [fetchSuggestions]);


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    if (newValue) {
      fetchSuggestions(newValue);
    } else {
      setSuggestions([]);
      setStatus('');
      setLoading(false);
      fetchSuggestions.cancel(); // Cancel pending debounced calls
    }
  };

  // Old hook state and functions removed
  /*
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      types: ['country', 'locality'],
    },
    debounce: 300,
  });
  */


  const handleSelect = async (description: string) => {
    console.log('City selected:', description);
    setValue(description); // Set value directly without fetching new suggestions
    setSuggestions([]); // Clear suggestions dropdown
    setStatus('');
    fetchSuggestions.cancel(); // Cancel pending debounced calls

    const isFirstCity = cities.size === 0;
    const city = await addCityByAddress(description, isFirstCity);
    if (city?.locationKey) {
      trackEvent.cityAdded(city.locationKey);
    }
    setValue(''); // Clear input after selection

    // Renew the session token after a selection
    if (isGoogleReady) {
        sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken();
    }
  };

  // Clear suggestions when clicking outside (using a simple approach for now)
  const handleBlur = () => {
      // Delay clearing to allow click event on suggestions to register
      setTimeout(() => {
          setSuggestions([]);
          setStatus('');
      }, 150);
  }

  return (
    // Consider adding onBlur to Autocomplete div if needed
    <Autocomplete className="transition-all duration-150 ease-in-out" onBlur={handleBlur}>
      <AutocompleteInput
        {...props}
        ref={ref}
        value={value}
        onChange={handleInputChange}
        // disabled={!isGoogleReady || loading} // Disable while loading or API not ready
        disabled={!isGoogleReady || loading}
        placeholder={props.placeholder ?? "Add City"}
      />
      {/* Show loading indicator? */}
      {/* {loading && <div>Loading...</div>} */}
      {status === google.maps.places.PlacesServiceStatus.OK && suggestions.length > 0 && (
        <AutocompleteList className="max-h-60 overflow-y-auto w-full">
          {suggestions.map((suggestion) => {
             // Suggestion is now PlacePrediction
             const place_id = suggestion.placeId; // Use placeId property
             // Use mainText/secondaryText properties directly from PlacePrediction
             const main_text_obj = suggestion.mainText;
             const secondary_text_obj = suggestion.secondaryText;
             const descriptionForSelect = suggestion.text.text; // Use full text for selection

             return (
              <AutocompleteItem 
                key={place_id} 
                onMouseDown={() => handleSelect(descriptionForSelect)} 
                // Use flex, handle overflow and whitespace for single-line truncation
                className="flex items-baseline w-full overflow-hidden whitespace-nowrap py-1"
              >
                {/* Main text - truncate if needed */}
                <strong className="truncate">
                  {main_text_obj?.text}
                </strong>
                {/* Secondary text - add margin, set color, truncate if needed */}
                {secondary_text_obj?.text && 
                  <small className="ml-2 text-gray-500 truncate">
                    {secondary_text_obj.text}
                  </small>
                }
              </AutocompleteItem>
            );
           }
          )}
        </AutocompleteList>
      )}
       {/* Optionally show error messages based on status */}
       {status && status !== google.maps.places.PlacesServiceStatus.OK && status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS && (
         // Basic error display, could be more user-friendly
         <div className="absolute z-10 mt-1 w-full bg-white border border-red-500 rounded-md shadow-lg p-2 text-red-700 text-sm">
           Autocomplete error: {status}
         </div>
       )}
    </Autocomplete>
  );
});

// Set display name for the component
CityAutocomplete.displayName = 'CityAutocomplete';

export default CityAutocomplete;
// ... existing code ... Need to ensure this is the last line
